import { Crosshair, ChevronDown, ChevronUp, X } from 'lucide-react';
import { useState } from 'react';
import { useTriggers } from '../../../lib/useTriggers';
import type { Account, AccountTrigger, UseCaseItem } from '../../../types';

export interface SelectedTriggerContext {
  title: string;
  why_now: string;
  evidence: string;
  angle: string;
}

interface Props {
  account: Account;
  selectedUseCase: UseCaseItem | null;
  selectedTrigger: SelectedTriggerContext | null;
  onSelect: (trigger: SelectedTriggerContext | null) => void;
}

const CONFIDENCE_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: 'bg-app-positive/10', text: 'text-app-positive' },
  medium: { bg: 'bg-app-warning/10', text: 'text-app-warning' },
  low: { bg: 'bg-app-canvas', text: 'text-app-muted' },
};

function filterRelevant(triggers: AccountTrigger[], useCase: UseCaseItem | null): AccountTrigger[] {
  if (!useCase) return triggers;
  const ucTitleLower = useCase.title.toLowerCase();
  const relevant = triggers.filter((t) => {
    if (Array.isArray(t.recommended_use_cases)) {
      for (const rec of t.recommended_use_cases) {
        if (typeof rec === 'string' && rec.toLowerCase().includes(ucTitleLower)) return true;
        if (typeof rec === 'string' && ucTitleLower.includes(rec.toLowerCase())) return true;
      }
    }
    return false;
  });
  return relevant.length > 0 ? relevant : triggers;
}

export default function TriggerContextSelector({ account, selectedUseCase, selectedTrigger, onSelect }: Props) {
  const { triggers } = useTriggers(account.id, account.org_id);
  const [expanded, setExpanded] = useState(false);

  if (triggers.length === 0 || account.signals_status !== 'completed') return null;

  const relevant = filterRelevant(triggers, selectedUseCase);

  const handleSelect = (trigger: AccountTrigger) => {
    onSelect({
      title: trigger.title,
      why_now: trigger.why_now,
      evidence: trigger.evidence_excerpt,
      angle: trigger.recommended_angle,
    });
    setExpanded(false);
  };

  return (
    <div className="rounded-lg border border-forest-200 bg-forest-50/30 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-forest-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Crosshair className="w-3.5 h-3.5 text-forest-600" />
          <span className="text-xs font-semibold text-forest-700">
            {selectedTrigger ? 'Trigger Context Applied' : 'Use Trigger Context'}
          </span>
          {!selectedTrigger && (
            <span className="text-[10px] text-forest-500">({relevant.length} available)</span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-forest-500" /> : <ChevronDown className="w-3.5 h-3.5 text-forest-500" />}
      </button>

      {selectedTrigger && !expanded && (
        <div className="px-4 pb-3 flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-forest-800 truncate">{selectedTrigger.title}</p>
            <p className="text-[10px] text-forest-600 line-clamp-1">{selectedTrigger.angle}</p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(null); }}
            className="p-1 text-forest-400 hover:text-app-critical hover:bg-app-critical/10 rounded transition-colors flex-shrink-0"
            title="Clear trigger context"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {expanded && (
        <div className="px-4 pb-3 border-t border-forest-200 pt-3 space-y-2 max-h-[300px] overflow-y-auto">
          {selectedTrigger && (
            <button
              onClick={() => { onSelect(null); setExpanded(false); }}
              className="w-full text-left p-2 text-xs text-app-muted hover:bg-app-canvas rounded-lg transition-colors"
            >
              Clear selection
            </button>
          )}
          {relevant.map((trigger) => {
            const conf = CONFIDENCE_COLORS[trigger.confidence] || CONFIDENCE_COLORS.low;
            const isActive = selectedTrigger?.title === trigger.title;
            return (
              <button
                key={trigger.id}
                onClick={() => handleSelect(trigger)}
                className={`w-full text-left p-3 rounded-lg border transition ${
                  isActive
                    ? 'border-forest-500 bg-forest-50 ring-1 ring-forest-200'
                    : 'border-app-line bg-app-surface hover:border-forest-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${conf.bg} ${conf.text}`}>
                    {trigger.confidence}
                  </span>
                  <span className="text-[10px] text-app-muted">
                    Urgency: {trigger.urgency_score}/10
                  </span>
                </div>
                <p className="text-xs font-medium text-app-ink leading-snug">{trigger.title}</p>
                <p className="text-[10px] text-app-muted mt-0.5 line-clamp-1">{trigger.why_now}</p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
